import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link as MuiLink,
  Alert,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Container,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/services/api";
import toast from "react-hot-toast";
import AgreementCheckbox from "@/components/auth/AgreementCheckbox";

interface RegisterFormData {
  name: string;
  phone: string;
  orgName: string;
  organizationType: string;
  password: string;
  confirmPassword: string;
  smsCode: string;
}

const registerSchema = yup.object({
  name: yup.string().min(2, "姓名至少2个字符").required("请输入姓名"),
  phone: yup
    .string()
    .matches(/^1[3-9]\d{9}$/, "请输入正确的手机号")
    .required("请输入手机号"),
  orgName: yup
    .string()
    .min(2, "单位名称至少2个字符")
    .required("请输入单位名称"),
  organizationType: yup
    .string()
    .required("请选择单位类型")
    .oneOf(["third_party", "government"], "请选择单位类型"),
  password: yup
    .string()
    .matches(/^\d{6}$/, "密码必须是6位数字")
    .required("请输入密码"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "两次输入的密码不一致")
    .required("请确认密码"),
  smsCode: yup
    .string()
    .matches(/^\d{6}$/, "验证码必须是6位数字")
    .required("请输入验证码"),
});

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [smsLoading, setSmsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneStatus, setPhoneStatus] = useState<
    "checking" | "available" | "taken" | null
  >(null);
  const [agreementChecked, setAgreementChecked] = useState(false);

  // 防抖定时器引用 - 使用 useRef 保持跨渲染的引用
  const phoneCheckTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      name: "",
      phone: "",
      orgName: "",
      organizationType: "third_party",
      password: "",
      confirmPassword: "",
      smsCode: "",
    },
  });

  const phoneValue = watch("phone");

  // 检查是否已登录
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      navigate("/");
    }
  }, [navigate]);

  // 检查手机号是否已注册
  const checkPhoneAvailability = async (phoneNumber: string) => {
    if (!phoneNumber || !/^1[3-9]\d{9}$/.test(phoneNumber)) {
      setPhoneStatus(null);
      return;
    }

    setPhoneChecking(true);
    setPhoneStatus("checking");

    try {
      const response = await apiClient.auth.checkPhone(phoneNumber);

      if (response.data?.exists) {
        setPhoneStatus("taken");
      } else {
        setPhoneStatus("available");
      }
    } catch (error) {
      setPhoneStatus(null);
    } finally {
      setPhoneChecking(false);
    }
  };

  // 手机号输入处理
  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;

    // 清除之前的定时器（防抖）
    if (phoneCheckTimerRef.current) {
      clearTimeout(phoneCheckTimerRef.current);
    }

    // 如果手机号格式正确，延迟检查
    if (/^1[3-9]\d{9}$/.test(value)) {
      phoneCheckTimerRef.current = setTimeout(() => {
        checkPhoneAvailability(value);
      }, 500); // 延迟500ms检查，避免频繁请求
    } else {
      setPhoneStatus(null);
    }
  };

  const sendSmsCode = async () => {
    if (!phoneValue || !/^1[3-9]\d{9}$/.test(phoneValue)) {
      toast.error("请输入正确的手机号");
      return;
    }

    if (phoneStatus !== "available") {
      toast.error("请等待手机号检查完成");
      return;
    }

    try {
      setSmsLoading(true);
      await apiClient.auth.sendSmsCode(phoneValue, 'register');
      toast.success("验证码已发送");
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      toast.error(err.message || "发送失败");
    } finally {
      setSmsLoading(false);
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    try {
      if (!agreementChecked) {
        toast.error("请先同意用户协议和隐私条款");
        return;
      }
      
      setLoading(true);
      setError("");

      const { confirmPassword, ...registerData } = data;
      await apiClient.auth.register(registerData);

      toast.success("注册成功");
      navigate("/login");
    } catch (err: any) {
      setError(err.message || "注册失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundImage: "url(/imgs/zybg3.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.3)", // 添加半透明遮罩层以确保文字可读性
          zIndex: 1,
        },
      }}
    >
      {/* 顶部居中大标题 */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 24, md: 32 },
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2,
          textAlign: "center",
          color: "#fff",
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: { xs: 36, md: 56, lg: 64 },
            letterSpacing: 2,
            textShadow: "0 4px 12px rgba(0,0,0,0.6)",
          }}
        >
          昭元绩效AI平台
        </Typography>
      </Box>

      <Container
        maxWidth="xl"
        sx={{
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          transform: "translateY(-50%)",
          px: { xs: 2, md: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 2,
        }}
      >
        {/* 左侧品牌与标语 */}
        <Box
          sx={{
            flex: 1,
            pr: { xs: 2, md: 6 },
            ml: { xs: 0, md: 2 },
            maxWidth: { md: "60%" },
            color: "#fff",
            textAlign: "left",
            alignSelf: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              mb: 2,
              textShadow: "0 2px 6px rgba(0,0,0,0.5)",
              fontSize: { xs: 22, md: 30, lg: 36 },
              lineHeight: 1.5,
              pl: 28,
            }}
          >
            用AI重新定义绩效
          </Typography>
          <Typography
            sx={{
              fontWeight: "bold",
              textShadow: "0 2px 6px rgba(0,0,0,0.5)",
              fontSize: { xs: 22, md: 30, lg: 36 },
              lineHeight: 1.5,
            }}
          >
            让重点项目评价更深刻，让绩效目标申报更简单
          </Typography>

          {/* 客服信息：放在标语下方 */}
          <Box
            sx={{
              mt: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {/* 微信二维码和免费试用提示 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2 }}>
              {/* 微信二维码 */}
              <Box sx={{ textAlign: "center" }}>
                <img
                  src="/imgs/kefu.png"
                  alt="客服微信二维码"
                  style={{
                    width: 160,
                    height: 160,
                    borderRadius: 8,
                    border: "2px solid rgba(255,255,255,0.3)",
                  }}
                />
              </Box>

              {/* 免费试用提示 - 在二维码旁边 */}
              <Typography
                sx={{
                  fontSize: { xs: 22, md: 30, lg: 36 },
                  color: "#FFA500",
                  fontWeight: "bold",
                  textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                  lineHeight: 1.3,
                }}
              >
                扫码添加客服微信
              </Typography>
            </Box>

            {/* 客服联系方式 */}
            <Box sx={{ width: 160 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.9)", textAlign: "left" }}
                >
                  客服电话：
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.9)", textAlign: "right" }}
                >
                  15515993457
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    textAlign: "left",
                    whiteSpace: "pre",
                  }}
                >
                  微       信：
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.9)", textAlign: "right" }}
                >
                  15515993457
                </Typography>
              </Box>
            </Box>

            {/* 产品文档包下载链接 */}
            <Box
              sx={{
                mt: 6, // 进一步增加上边距
                pt: 3, // 增加内边距
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "600%", // 增加长度到6倍
                  height: "1px",
                  backgroundColor: "#A5A5A5",
                },
              }}
            >
              <MuiLink
                href="/api/download/all-docs"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "14px",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "#ffffff",
                  },
                }}
              >
                📦 产品文档包
              </MuiLink>
            </Box>
          </Box>
        </Box>

        {/* 右侧注册卡片 - 居中垂直，靠右 */}
        <Box sx={{ width: 480, ml: "auto", mr: { xs: 2, md: 8 } }}>
          <Paper
            elevation={6}
            sx={{
              p: 4,
              borderRadius: 3,
              width: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.98)", // 增加不透明度，确保在暗色模式下有足够对比度
              backdropFilter: "blur(10px)", // 毛玻璃效果
              border: "1px solid rgba(255, 255, 255, 0.3)",
              // 确保在暗色模式下文字可见
              "& .MuiTypography-root": {
                color: "rgba(0, 0, 0, 0.87)",
              },
            }}
          >
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                注册
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextField
                margin="dense"
                required
                fullWidth
                id="name"
                label="姓名"
                autoComplete="name"
                autoFocus
                {...register("name")}
                disabled={loading}
                placeholder="请输入您的姓名"
                error={!!errors.name}
                helperText={errors.name?.message}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(0, 0, 0, 0.7)",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "rgba(0, 0, 0, 0.87)",
                    "&::placeholder": {
                      color: "rgba(0, 0, 0, 0.5)",
                      opacity: 1,
                    },
                  },
                }}
              />

              {/* 手机号 */}
              <TextField
                margin="dense"
                required
                fullWidth
                id="phone"
                label="手机号"
                autoComplete="tel"
                {...register("phone")}
                onChange={(e) => {
                  register("phone").onChange(e);
                  handlePhoneChange(e);
                }}
                disabled={loading}
                placeholder="请输入手机号"
                error={!!errors.phone || phoneStatus === "taken"}
                helperText={
                  phoneChecking
                    ? "正在检查手机号..."
                    : phoneStatus === "available"
                      ? "✓ 手机号可用"
                      : phoneStatus === "taken"
                        ? "该手机号已注册"
                        : errors.phone?.message || ""
                }
                InputProps={{
                  endAdornment: phoneChecking ? (
                    <CircularProgress size={20} />
                  ) : phoneStatus === "available" ? (
                    <span style={{ color: "#4caf50", fontSize: "20px" }}>
                      ✓
                    </span>
                  ) : phoneStatus === "taken" ? (
                    <span style={{ color: "#f44336", fontSize: "20px" }}>
                      ✗
                    </span>
                  ) : null,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(0, 0, 0, 0.7)",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "rgba(0, 0, 0, 0.87)",
                    "&::placeholder": {
                      color: "rgba(0, 0, 0, 0.5)",
                      opacity: 1,
                    },
                  },
                }}
              />

              {/* 手机验证码 + 发送按钮 */}
              <Box
                sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}
              >
                <TextField
                  fullWidth
                  label="短信验证码"
                  {...register("smsCode")}
                  disabled={loading}
                  placeholder="请输入6位验证码"
                  error={!!errors.smsCode}
                  helperText={errors.smsCode?.message}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={sendSmsCode}
                  disabled={
                    smsLoading ||
                    countdown > 0 ||
                    loading ||
                    phoneStatus !== "available"
                  }
                  sx={{ whiteSpace: "nowrap", height: "40px" }}
                >
                  {countdown > 0 ? `${countdown}s` : "发送验证码"}
                </Button>
              </Box>

              <TextField
                margin="dense"
                required
                fullWidth
                id="orgName"
                label="单位名称"
                autoComplete="organization"
                {...register("orgName")}
                disabled={loading}
                placeholder="请输入所属单位全称"
                error={!!errors.orgName}
                helperText={errors.orgName?.message}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(0, 0, 0, 0.7)",
                    "&.Mui-focused": {
                      color: "primary.main",
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "rgba(0, 0, 0, 0.87)",
                    "&::placeholder": {
                      color: "rgba(0, 0, 0, 0.5)",
                      opacity: 1,
                    },
                  },
                }}
              />

              <FormControl fullWidth margin="dense" variant="outlined" disabled={loading}>
                <InputLabel id="organization-type-label">单位类型</InputLabel>
                <Select
                  labelId="organization-type-label"
                  id="organizationType"
                  label="单位类型"
                  {...register("organizationType")}
                  error={!!errors.organizationType}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 1)",
                    },
                  }}
                >
                  <MenuItem value="third_party">第三方公司</MenuItem>
                  <MenuItem value="government">政府单位</MenuItem>
                </Select>
                {errors.organizationType && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                    {errors.organizationType.message}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel htmlFor="password">登录密码</InputLabel>
                <OutlinedInput
                  id="password"
                  type={showPassword ? "text" : "password"}
                  {...register("password")}
                  error={!!errors.password}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="登录密码"
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    },
                  }}
                />
              </FormControl>
              {errors.password && (
                <Typography color="error" variant="caption">
                  {errors.password.message}
                </Typography>
              )}

              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel htmlFor="confirmPassword">确认密码</InputLabel>
                <OutlinedInput
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  {...register("confirmPassword")}
                  error={!!errors.confirmPassword}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="确认密码"
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    },
                  }}
                />
              </FormControl>
              {errors.confirmPassword && (
                <Typography color="error" variant="caption">
                  {errors.confirmPassword.message}
                </Typography>
              )}

              <AgreementCheckbox
                checked={agreementChecked}
                onChange={setAgreementChecked}
                disabled={loading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2, mb: 1, py: 1.5 }}
                disabled={loading || phoneStatus !== "available" || !agreementChecked}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "立即注册"
                )}
              </Button>

              {/* 登录链接 */}
              <Box sx={{ textAlign: "center", mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  已有账号？
                  <Typography
                    component="span"
                    onClick={() => navigate("/login")}
                    sx={{
                      color: "primary.main",
                      ml: 1,
                      fontWeight: 600,
                      textDecoration: "underline",
                      cursor: "pointer",
                      "&:hover": {
                        color: "primary.dark",
                      },
                    }}
                  >
                    立即登录
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* 备案信息 - 固定在页面底部 */}
      <Box
        sx={{
          textAlign: "center",
          py: 2,
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px)",
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 3,
        }}
      >
        <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
          昭元绩效AI平台
          <MuiLink
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              textDecoration: "none",
              ml: 1,
              "&:hover": {
                textDecoration: "underline",
                color: "#ffffff",
              },
            }}
          >
            豫ICP备2025140414号-1
          </MuiLink>
        </Typography>
      </Box>
    </Box>
  );
};

export default RegisterPage;
